import { useEffect, useRef, useState } from 'react';

interface TabVisibilityOptions {
  onTabReturn?: (timeHidden: number) => void;
  onTabHidden?: () => void;
  debounceMs?: number;
  minHiddenTimeForAction?: number;
}

export const useTabVisibility = (options: TabVisibilityOptions = {}) => {
  const {
    onTabReturn,
    onTabHidden,
    debounceMs = 1000,
    minHiddenTimeForAction = 2000
  } = options;

  const [isTabVisible, setIsTabVisible] = useState(true);
  const [wasTabHiddenDuringOperation, setWasTabHiddenDuringOperation] = useState(false);
  const lastVisibleTime = useRef(Date.now());
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const timeHidden = now - lastVisibleTime.current;
        
        setIsTabVisible(true);
        
        // Clear any pending debounced actions
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
          debounceTimer.current = null;
        }
        
        // Only trigger action if tab was hidden for a significant amount of time
        if (timeHidden > minHiddenTimeForAction && onTabReturn) {
          // Debounce the action to prevent rapid firing
          debounceTimer.current = setTimeout(() => {
            onTabReturn(timeHidden);
            debounceTimer.current = null;
          }, debounceMs);
        }
        
        // Reset the operation flag after a delay
        setTimeout(() => {
          setWasTabHiddenDuringOperation(false);
        }, 500);
        
      } else {
        setIsTabVisible(false);
        lastVisibleTime.current = Date.now();
        setWasTabHiddenDuringOperation(true);
        
        if (onTabHidden) {
          onTabHidden();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [onTabReturn, onTabHidden, debounceMs, minHiddenTimeForAction]);

  return {
    isTabVisible,
    wasTabHiddenDuringOperation,
    setWasTabHiddenDuringOperation
  };
}; 